/**
 * The countdown to the player's next league fight, where it's always in sight (D50): on the
 * League tab of the desktop header ("05:12:33", a pulsing "Live" while the window is open), and
 * as a chip on the phone Home's hero, above the fold. Nothing on a day without a fight for them,
 * or for a player without a league place.
 */
import { LEAGUES, type DayInfo } from '@caged/shared';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { useRegistered, useSecondNow } from '../hooks';
import { fightPhase, todayFixture, useFightCountdown } from '../league';
import s from './Chrome.module.css';
import ui from './ui.module.css';

/**
 * The next league day when the player has a fight on it still to fight, in a season still on (not
 * when their place's bot fights it: placed after the fight began, they start the next day).
 */
function useMyNextFight(): DayInfo | null {
  const registered = useRegistered();
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  useEffect(() => {
    if (registered) void load();
  }, [registered, load]);
  if (mine?.status !== 'ok' || mine.daysDone >= LEAGUES.seasonDays) return null;
  const { fixture } = todayFixture(mine, mine.me.slot, mine.me.joinedDay);
  return fixture && fixture.state !== 'done' ? mine.next : null;
}

export function LeagueNavBadge() {
  const next = useMyNextFight();
  return next ? <NavTicking next={next} /> : null;
}

/** Re-renders every second on its own, not the whole header. */
function NavTicking({ next }: { next: DayInfo }) {
  const { t } = useTranslation();
  const now = useSecondNow();
  const countdown = useFightCountdown();
  const phase = fightPhase(next, now);
  if (phase === 'live') {
    return (
      <span className={[s.leagueBadge, s.leagueBadgeLive].join(' ')}>
        <span className={s.leagueDot} aria-hidden="true" />
        {t('screens.league.live')}
      </span>
    );
  }
  if (phase !== 'upcoming') return null;
  const left = countdown(next.fightAt - now);
  return (
    <>
      <span className={s.leagueBadge} aria-hidden="true">
        {left}
      </span>
      <span className={ui.srOnly}>{t('screens.home.leagueFightIn', { time: left })}</span>
    </>
  );
}

/** The phone Home's hero chip: "League" over the countdown; "Live" while the window is open. */
export function LeagueHeroChip({ className }: { className?: string }) {
  const next = useMyNextFight();
  return next ? <HeroTicking next={next} className={className} /> : null;
}

function HeroTicking({ next, className }: { next: DayInfo; className?: string }) {
  const { t } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const now = useSecondNow();
  const countdown = useFightCountdown();
  const phase = fightPhase(next, now);
  if (phase !== 'upcoming' && phase !== 'live') return null;
  const live = phase === 'live';
  const left = live ? '' : countdown(next.fightAt - now);
  return (
    <button
      type="button"
      className={[s.heroChip, live ? s.heroChipLive : '', className ?? ''].join(' ')}
      onClick={() => openLeague('league')}
      aria-label={live ? t('screens.home.leagueLive') : t('screens.home.leagueFightIn', { time: left })}
    >
      <span className={s.heroChipLabel} aria-hidden="true">
        {live && <span className={s.leagueDot} />}
        {t('screens.nav.league')}
      </span>
      <span className={s.heroChipTime} aria-hidden="true">
        {live ? t('screens.league.live') : left}
      </span>
    </button>
  );
}
